import { t } from "i18next";
import { InvalidTokenError, jwtDecode } from "jwt-decode";

export const getAccessToken = () => localStorage.getItem("accessToken");
export const getRefreshToken = () => localStorage.getItem("refreshToken");

// Mirror the access token in a browser cookie in addition to
// localStorage. The cookie is what nginx-proxy reads when it
// makes the /api/v1/login/verify-grafana-access sub-request before
// proxying /monitoring/* to Grafana — localStorage isn't visible to
// the network layer. Cookie expiry mirrors the token's `exp` claim
// so a stale cookie can't outlive its JWT.
const ACCESS_TOKEN_COOKIE = "access_token";

const setAccessTokenCookie = (token) => {
  try {
    const { exp } = jwtDecode(token);
    const maxAge = Math.max(0, exp - Math.floor(Date.now() / 1000));
    const secure = window.location.protocol === "https:" ? " secure;" : "";
    document.cookie =
      `${ACCESS_TOKEN_COOKIE}=${token}; path=/;${secure} samesite=lax;` +
      ` max-age=${maxAge}`;
  } catch {
    // jwtDecode shouldn't fail on a server-issued token, but if it
    // does we still want localStorage to work — skip the cookie.
  }
};

const clearAccessTokenCookie = () => {
  document.cookie = `${ACCESS_TOKEN_COOKIE}=; path=/; max-age=0`;
};

export const setAccessToken = (token) => {
  localStorage.setItem("accessToken", token);
  setAccessTokenCookie(token);
};
export const setRefreshToken = (token) =>
  localStorage.setItem("refreshToken", token);

export const removeAccessToken = () => {
  localStorage.removeItem("accessToken");
  clearAccessTokenCookie();
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  const { exp } = jwtDecode(token);
  if (!exp) return true;
  return Date.now() >= exp * 1000;
};

// The tabs of a browser share one session: its tokens, here in localStorage. A
// sign-out removes them (authSlice's logout); a renewal or a sign-in only
// replaces them. True when a change another tab made to localStorage (its
// `storage` event) took the tokens away: a token removed, or the storage
// cleared. The tokens must still be gone when this tab hears of it, so a
// sign-in that followed at once is not undone here.
const SESSION_TOKEN_KEYS = ["accessToken", "refreshToken"];

export const isSignedOutInAnotherTab = (event) =>
  event.storageArea === localStorage &&
  (event.key === null || SESSION_TOKEN_KEYS.includes(event.key)) &&
  event.newValue === null &&
  !getAccessToken();

// One renewal at a time. A page that loads several queries at once gets them
// all refused when the access token expires; each renewal returns a new pair
// and revokes the previous one, so renewals started side by side revoked each
// other's tokens and the retries failed. Callers that arrive while a renewal
// is under way get that renewal's result.
//
// One at a time across the tabs of this browser as well. The tabs share
// localStorage and a refresh token renews once: two tabs renewing at the same
// moment had one refused, and its sign-out cleared the pair the other tab had
// just stored. A tab that waited for the lock finds the other tab's pair.
//
// A failed renewal is judged here, the same for every caller (see below).
let renewalInProgress = null;
const RENEWAL_LOCK = "rf-sedra-session-renewal";

export const refreshToken = () => {
  if (!renewalInProgress) {
    const seen = getRefreshToken();
    renewalInProgress = acrossTabs(() => renewUnlessRenewed(seen))
      .catch(settleFailedRenewal)
      .finally(() => {
        renewalInProgress = null;
      });
  }
  return renewalInProgress;
};

// Only the server's refusal ends the session (#121). It refuses a renewal with
// 401 (the refresh token was spent, revoked, has expired or is broken) or 403
// (the account is no longer active), and a session without a usable refresh
// token cannot be renewed at all: the user is signed out, here, whoever asked
// for the renewal, and the other tabs follow (store.js, #118).
//
// Any other failure — no answer, a 5xx while a deploy restarts the backend, any
// other status — says nothing about the session, which the server still holds:
// counted as a refusal, it signed out every tab of every user whose access
// token had expired. The stored tokens are kept, and the renewal rejects with
// PortalUnreachableError; the request that needed the renewal fails with its
// message and may be tried again. Nothing here tries it again by itself.
class SessionRefusedError extends Error {}

export class PortalUnreachableError extends Error {
  constructor(cause) {
    // The texts load after the page starts, and a renewal can fail before
    // they have (a reload while the backend restarts): the English text then
    // stands in for the translation.
    super(
      t("common:portalUnreachable", {
        defaultValue: "The portal could not be reached; please try again.",
      }),
      { cause }
    );
    this.name = "PortalUnreachableError";
  }
}

// Signing out is the store's: authSlice's logout, which also empties the API
// caches. store.js hands it over once; the renewal alone decides when.
let signOut = () => {};

export const onSessionRefused = (callback) => {
  signOut = callback;
};

const settleFailedRenewal = (error) => {
  if (error instanceof SessionRefusedError) {
    signOut();
    throw error;
  }
  throw new PortalUnreachableError(error);
};

const REFUSALS = [401, 403];

// A refresh token the next renewal can send: present, readable, not expired.
const isRenewable = (token) => {
  try {
    return !isTokenExpired(token);
  } catch (error) {
    if (error instanceof InvalidTokenError) return false;
    throw error;
  }
};

const acrossTabs = (task) =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request(RENEWAL_LOCK, task)
    : task();

const renewUnlessRenewed = (seen) => {
  const current = getRefreshToken();
  if (current && current !== seen && getAccessToken()) {
    return Promise.resolve(getAccessToken());
  }
  return renew();
};

const renew = async () => {
  const refreshToken = getRefreshToken();
  if (!isRenewable(refreshToken)) {
    throw new SessionRefusedError("Refresh token expired");
  }

  // No answer at all rejects here: not a refusal.
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/login/new_access_token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }
  );

  if (!response.ok) {
    // A refresh token renews once. Refused because another tab of this browser
    // renewed first: that tab has stored the new pair, which this one shares.
    if (getRefreshToken() !== refreshToken && getAccessToken()) {
      return getAccessToken();
    }
    if (REFUSALS.includes(response.status)) {
      throw new SessionRefusedError("Failed to refresh token");
    }
    throw new Error(`The renewal was answered with ${response.status}`);
  }

  const data = await response.json();
  // A renewal rotates the pair: the backend revokes the refresh token sent
  // here (generate_auth_token), so only the one returned is accepted by the
  // next renewal. Keeping the old one signed users out at the second renewal.
  const { access_token, refresh_token } = data.data;

  setAccessToken(access_token);
  setRefreshToken(refresh_token);

  return access_token;
};
